import type { GccDashboardData, GccDispositionTag } from '@shared/types'
import { GCC_TOOLTIPS } from './gcc-tooltips'
import { filterExcludedTaggedTasks } from './s0bExclusions'

const MEANING: Record<GccDispositionTag, string> = {
  Eliminate: GCC_TOOLTIPS.dispositionEliminate,
  Agentify: GCC_TOOLTIPS.dispositionAgentify,
  Automate: GCC_TOOLTIPS.dispositionAutomate,
  Augment: GCC_TOOLTIPS.dispositionAugment,
  Human: GCC_TOOLTIPS.dispositionHuman,
}

export interface DispositionTooltipInput {
  /** Tasks shown in this disposition group */
  count: number
  /** Tasks shown on the Disposition tab */
  total: number
  /** Share of a task's work left for people, from the design's residual table; absent on v1 designs */
  residualPct?: number
  /** Tasks tagged Eliminate that the S0b review excluded from the tab */
  excludedEliminate: number
}

/**
 * Tooltip lines for a Disposition tab pill (WI 2576): what the disposition means,
 * the work it leaves for people and, when Eliminate shows 0, why.
 */
export function dispositionTooltip(tag: GccDispositionTag, { count, total, residualPct, excludedEliminate }: DispositionTooltipInput): string[] {
  const lines = [MEANING[tag]]
  if (residualPct !== undefined) lines.push(`Work left for people: ${residualPct}% of each task’s FTE.`)
  if (tag === 'Eliminate' && count === 0) lines.push(zeroEliminateReason(total, excludedEliminate))
  return lines
}

function zeroEliminateReason(total: number, excluded: number): string {
  if (excluded > 0) {
    const one = excluded === 1
    return `Why 0: ${excluded} task${one ? '' : 's'} tagged Eliminate ${one ? 'was' : 'were'} excluded in the task register review, so ${one ? 'it is' : 'they are'} not counted.`
  }
  return total === 0 ? GCC_TOOLTIPS.dispositionEliminateZeroNoTasks : GCC_TOOLTIPS.dispositionEliminateZeroNone
}

/** Tasks tagged Eliminate that the S0b review's exclusions hide from the Disposition tab. */
export function countExcludedEliminate(gcc: GccDashboardData): number {
  const isEliminate = (t: { disposition: GccDispositionTag }) => t.disposition === 'Eliminate'
  const all = (gcc.taggedTasks?.taggedTasks ?? []).filter(isEliminate).length
  return all - filterExcludedTaggedTasks(gcc).filter(isEliminate).length
}
